import { useEffect, useMemo } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { videos, categories } from '@/mocks/videos';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname]);

  const video = useMemo(() => videos.find((v) => v.id === id), [id]);
  const relatedVideos = useMemo(() => {
    if (!video) return [];
    return videos.filter((v) => v.categorySlug === video.categorySlug && v.id !== video.id).slice(0, 4);
  }, [video]);

  if (!video) {
    return (
      <>
        <Navbar />
        <main className="pt-20 pb-14 bg-background-50 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-error-warning-line text-3xl"></i>
            </div>
            <h1 className="font-heading text-xl font-bold text-foreground-900 mb-2">视频未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">您要查看的视频不存在或已被移除</p>
            <Link to="/category/all" className="bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium px-6 py-2.5 rounded-full whitespace-nowrap transition-all cursor-pointer inline-flex items-center gap-2">
              <i className="ri-arrow-left-line"></i> 返回浏览
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const category = categories.find((c) => c.slug === video.categorySlug);

  const breadcrumbItems = [
    { name: '首页', path: '/' },
    { name: '分类浏览', path: '/category/all' },
    ...(category ? [{ name: category.name, path: `/category/${video.categorySlug}` }] : []),
    { name: video.title },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: video.title,
    description: video.description,
    datePublished: video.date,
    image: video.thumbnail,
    author: {
      '@type': 'Organization',
      name: '麻豆cm传媒在线高清',
    },
    publisher: {
      '@type': 'Organization',
      name: '麻豆cm传媒在线高清',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://madoucm.com/article/${video.id}`,
    },
  };

  return (
    <>
      <title>{`${video.title} - 麻豆cm传媒在线高清`}</title>
      <meta name="description" content={video.description} />
      <meta name="keywords" content={`${video.title},麻豆cm传媒在线高清,${video.category},高清视频,在线播放`} />
      <link rel="canonical" href={`https://madoucm.com/article/${video.id}`} />
      <meta property="og:title" content={`${video.title} - 麻豆cm传媒在线高清`} />
      <meta property="og:description" content={video.description} />
      <meta property="og:type" content="article" />
      <meta property="og:url" content={`https://madoucm.com/article/${video.id}`} />
      <meta property="og:image" content={video.thumbnail} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={`${video.title} - 麻豆cm传媒在线高清`} />
      <meta name="twitter:description" content={video.description} />
      <meta name="twitter:image" content={video.thumbnail} />
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>

      <Navbar />
      <main className="pt-20 pb-14 bg-background-50 min-h-screen">
        <article className="max-w-[1100px] mx-auto px-4 md:px-6">
          <div className="mb-4">
            <Breadcrumb items={breadcrumbItems} />
          </div>

          <div className="relative w-full aspect-video rounded-xl overflow-hidden mb-6 bg-foreground-950">
            <img
              src={video.thumbnail}
              alt={video.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
              <div className="w-20 h-20 md:w-24 md:h-24 flex items-center justify-center rounded-full bg-primary-500/90 hover:bg-primary-500 transition-all cursor-pointer group">
                <i className="ri-play-fill text-background-50 text-3xl md:text-4xl ml-1 group-hover:scale-110 transition-transform"></i>
              </div>
            </div>
            <div className="absolute bottom-4 right-4 flex items-center gap-2">
              <span className="bg-background-950/75 backdrop-blur-sm text-background-50 text-xs px-2.5 py-1 rounded-md">{video.resolution}</span>
              <span className="bg-background-950/75 backdrop-blur-sm text-background-50 text-xs px-2.5 py-1 rounded-md">{video.duration}</span>
            </div>
          </div>

          <div className="flex flex-col lg:flex-row gap-8">
            <div className="flex-1">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                {video.title}
              </h1>

              <div className="flex flex-wrap items-center gap-3 mb-6 text-sm text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i> {video.views} 次观看
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i> {video.date}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-film-line"></i> {video.duration}
                </span>
                <span className="bg-secondary-100 text-secondary-700 text-xs px-2.5 py-1 rounded-full">
                  {video.category}
                </span>
              </div>

              <div className="flex flex-wrap gap-2 mb-6">
                {video.tags.map((tag) => (
                  <span key={tag} className="text-xs bg-background-100 text-foreground-500 px-3 py-1 rounded-full border border-background-200/70">
                    {tag}
                  </span>
                ))}
              </div>

              <div className="bg-background-100 rounded-xl p-5 md:p-6 mb-8">
                <h2 className="font-heading font-semibold text-foreground-900 mb-3 text-lg">视频简介</h2>
                <p className="text-sm text-foreground-600 leading-relaxed">{video.description}</p>
              </div>
            </div>

            <div className="lg:w-[320px] flex-shrink-0">
              <div className="bg-background-100 rounded-xl p-5 sticky top-24">
                <h3 className="font-heading font-semibold text-foreground-900 mb-4 text-sm">相关推荐</h3>
                <div className="flex flex-col gap-3">
                  {relatedVideos.map((rv) => (
                    <Link
                      key={rv.id}
                      to={`/article/${rv.id}`}
                      className="flex gap-3 group cursor-pointer"
                    >
                      <div className="w-[120px] aspect-video rounded-lg overflow-hidden flex-shrink-0">
                        <img
                          src={rv.thumbnail}
                          alt={rv.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-xs font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-500 transition-colors">
                          {rv.title}
                        </h4>
                        <p className="text-[10px] text-foreground-400 mt-1">{rv.views} 次观看</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}